import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import type { PortfolioIdResponseDto } from '../../api-schema/generated/types';

const coreService = apiService.getService(ServiceType.CORE);

export type AccountType = 'EQUITY' | 'DERIVATIVES';

export interface Portfolio {
    id: string;
    portfolioId: string;
    portfolioName: string;
    createdDate: string;
    status: 'active' | 'inactive' | 'closed';
    /** Pinetree sub-account type. Pair-trading filters portfolios by this when
     *  wiring the stock vs deriv leg of a pair. Optional because legacy /portfolios
     *  responses may not include it; UI should default to EQUITY when absent. */
    accountType?: AccountType;
    /** CLIENT: the customer's terminal sends this account's orders and starts its bots. */
    brokerMode?: 'SERVER' | 'CLIENT';
}

function mapPortfolioListItem(item: PortfolioIdResponseDto): Portfolio {
    return {
        id: item.id,
        portfolioId: item.id,
        portfolioName: item.name ?? item.id,
        createdDate: '',
        status: 'active',
        accountType: item.accountType as AccountType | undefined,
        brokerMode: (item as { brokerMode?: 'SERVER' | 'CLIENT' }).brokerMode,
    };
}

interface UsePortfoliosProps {
    initialPortfolioId?: string | null;
    /** When set, backend filters portfolios by sub-account type. Pass 'EQUITY' on pages
     *  like grid trading that don't support derivatives. */
    accountType?: AccountType;
}

export const usePortfolios = ({ initialPortfolioId, accountType }: UsePortfoliosProps = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
    const [loading, setLoading] = useState(true);
    // Read inside `fetchPortfolios` without making it a dependency — depending on the
    // selection would refetch every time the selection changed.
    const selectedRef = useRef('');
    useEffect(() => { selectedRef.current = selectedPortfolioId; }, [selectedPortfolioId]);

    const fetchPortfolios = useCallback(async () => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                message.error('Please login first');
                navigate('/signin');
                return;
            }

            const response = await coreService.get<PortfolioIdResponseDto[]>('/portfolios', {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params: accountType ? { accountType } : undefined,
            });

            const raw = Array.isArray(response.data) ? response.data : [];
            const fetchedPortfolios = raw.map(mapPortfolioListItem);
            setPortfolios(fetchedPortfolios);

            // A re-run must not move the user. This callback's deps (`location.state`,
            // `navigate`, `accountType`) can change for reasons that have nothing to do with
            // which portfolio is being looked at, and falling through to `[0]` below switched
            // the page mid-session — taking the grid page with it, config and all.
            // `location.state` only speaks for the navigation that opened the page, so a
            // selection already on screen outranks it.
            if (selectedRef.current && fetchedPortfolios.some((p) => p.id === selectedRef.current)) {
                setLoading(false);
                return;
            }

            // Set portfolio from navigation state, initialPortfolioId, or first available
            if (location.state?.portfolio?.id) {
                const navPortfolio = fetchedPortfolios.find((p: Portfolio) => p.id === location.state.portfolio.id);
                if (navPortfolio) {
                    setSelectedPortfolioId(navPortfolio.id);
                } else if (fetchedPortfolios.length > 0) {
                    setSelectedPortfolioId(fetchedPortfolios[0].id);
                }
            } else if (initialPortfolioId) {
                const initPortfolio = fetchedPortfolios.find((p: Portfolio) => p.id === initialPortfolioId);
                if (initPortfolio) {
                    setSelectedPortfolioId(initPortfolio.id);
                } else if (fetchedPortfolios.length > 0) {
                    setSelectedPortfolioId(fetchedPortfolios[0].id);
                }
            } else if (fetchedPortfolios.length > 0) {
                setSelectedPortfolioId(fetchedPortfolios[0].id);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching portfolios:', error);
            message.error('Failed to load portfolios');
            setLoading(false);
        }
    }, [navigate, location.state, initialPortfolioId, accountType]);

    useEffect(() => {
        fetchPortfolios();
    }, [fetchPortfolios]);

    const selectedPortfolio = portfolios.find(p => p.id === selectedPortfolioId);

    return {
        portfolios,
        selectedPortfolioId,
        setSelectedPortfolioId,
        selectedPortfolio,
        loading,
        refetch: fetchPortfolios
    };
};

